import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

type AlertProviderProps = {
  children: ReactNode;
};

type AlertContextType = (
  message: string,
  severity?: 'error' | 'warning' | 'info' | 'success',
) => void;

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const AUTO_HIDE_DURATION_MS = 3000;
  const TIMEOUT_MS = 3000;
  const [open, setOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [severity, setSeverity] = useState<
    'error' | 'warning' | 'info' | 'success'
  >('info');

  const showAlert: AlertContextType = (newMessage, newSeverity = 'info') => {
    setMessage(newMessage);
    setSeverity(newSeverity);
    setOpen(true);
  };

  const hideAlert = () => {
    setOpen(false);
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (open) {
      timer = setTimeout(() => {
        hideAlert();
      }, TIMEOUT_MS);
    }
    return () => clearTimeout(timer);
  }, [open]);

  return (
    <AlertContext.Provider value={showAlert}>
      {children}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={open}
        autoHideDuration={AUTO_HIDE_DURATION_MS}
      >
        <Alert
          onClose={hideAlert}
          severity={severity}
          variant="filled"
          className="alert-custom"
        >
          {message}
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
};

const MemoizedComponent = React.memo(AlertProvider);
export default MemoizedComponent;
